import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { MENU_FLOWS } from "@/constants/global";
import { useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { useUpdateBranchMutation } from "@/features/organizations/api/branchApi";
import { updateOrganization } from "@/features/organizations/slice";
import useSnackbar from "@/hooks/useSnackbar";

export const useDeliveryControl = () => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();

  const activeOrganizationId = useAppSelector((state) => state.ui.activeOrganization);
  const activeBranchId = useAppSelector((state) => state.ui.activeBranch);
  const organizations = useAppSelector((state) => state.organizations);

  const currentOrganization = useMemo(() => {
    if (!activeOrganizationId) return null;
    return organizations[activeOrganizationId] ?? null;
  }, [organizations, activeOrganizationId]);

  const activeBranch = useMemo(() => {
    if (!currentOrganization?.branches || !activeBranchId) return null;
    return currentOrganization.branches.find((b) => b.id === activeBranchId) ?? null;
  }, [currentOrganization?.branches, activeBranchId]);

  const scope: "organization" | "branch" = activeBranch ? "branch" : "organization";

  const isDeliveryEnabled = useMemo(() => {
    if (scope === "branch" && activeBranch) {
      const DEFAULT_SERVICES = [
        MENU_FLOWS.DELIVERY,
        MENU_FLOWS.PICK_UP,
        MENU_FLOWS.DINE_IN,
      ];
      const services = activeBranch.services ?? DEFAULT_SERVICES;
      return services.includes(MENU_FLOWS.DELIVERY);
    }

    return currentOrganization?.settings?.services?.delivery?.isEnabled !== false;
  }, [scope, activeBranch, currentOrganization?.settings?.services?.delivery?.isEnabled]);

  const [updateOrganizationMutation, { isLoading: isUpdatingOrg }] = useUpdateOrganizationMutation();
  const [updateBranchMutation, { isLoading: isUpdatingBranch }] = useUpdateBranchMutation();
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const isLoading = isUpdatingOrg || isUpdatingBranch || isLocalLoading;

  const toggleDelivery = async (explicitStatus?: boolean) => {
    const nextStatus = explicitStatus !== undefined ? explicitStatus : !isDeliveryEnabled;
    setIsLocalLoading(true);

    try {
      if (scope === "branch" && activeBranch && activeOrganizationId) {
        const DEFAULT_SERVICES = [
          MENU_FLOWS.DELIVERY,
          MENU_FLOWS.PICK_UP,
          MENU_FLOWS.DINE_IN,
        ];
        const currentServices = activeBranch.services ?? DEFAULT_SERVICES;

        let newServices: string[];
        if (nextStatus) {
          newServices = currentServices.includes(MENU_FLOWS.DELIVERY)
            ? currentServices
            : [...currentServices, MENU_FLOWS.DELIVERY];
        } else {
          newServices = currentServices.filter((s) => s !== MENU_FLOWS.DELIVERY);
        }

        await updateBranchMutation({
          organizationId: activeOrganizationId,
          branchId: activeBranch.id,
          branch: {
            ...activeBranch,
            services: newServices,
          },
        }).unwrap();

        openSnackbar(
          nextStatus ? "Delivery activado para la sucursal" : "Delivery desactivado para la sucursal",
          "success"
        );
      } else if (currentOrganization && activeOrganizationId) {
        const updatedSettings = {
          ...currentOrganization.settings,
          services: {
            ...currentOrganization.settings?.services,
            delivery: {
              ...currentOrganization.settings?.services?.delivery,
              isEnabled: nextStatus,
            },
          },
        };

        await updateOrganizationMutation({
          organizationId: activeOrganizationId,
          payload: {
            settings: updatedSettings,
          },
        }).unwrap();

        dispatch(
          updateOrganization({
            id: activeOrganizationId,
            settings: updatedSettings,
          })
        );

        openSnackbar(
          nextStatus ? "Delivery activado para la organización" : "Delivery desactivado para la organización",
          "success"
        );
      }
    } catch (error) {
      console.error("Error toggling delivery:", error);
      openSnackbar("Error al actualizar la configuración de Delivery", "error");
    } finally {
      setIsLocalLoading(false);
    }
  };

  return {
    isDeliveryEnabled,
    toggleDelivery,
    isLoading,
    scope,
    activeOrganizationId,
    activeBranchId,
    activeBranch,
    currentOrganization,
  };
};

export default useDeliveryControl;
